import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Field, FormSection } from '@/components/catalog/CatalogParts';
import { PasswordInput } from '@/components/catalog/PasswordParts';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import {
  useChangeLoginPassword,
  useDeleteOnlineLogin,
  useSaveOnlineLogin,
  type PasswordChange,
} from '@/features/onlineAccounts/hooks';
import {
  LOGIN_ROLE_LABELS,
  friendlyOnlineAccountError,
  loginLabel,
  type OnlineLogin,
} from '@/features/onlineAccounts/model';
import type { SeatProfile } from '@/features/software/model';
import { formatDate } from '@/lib/formatters';
import type { OnlineLoginRole } from '@/types/database';

const NOBODY = '__none';
const SOMEONE_ELSE = '__other';

type PasswordMode = 'keep' | 'set' | 'clear';

export function OnlineLoginDialog({
  open,
  login,
  accountId,
  accountName,
  defaultRole,
  people,
  canEditLogin,
  canChangePassword,
  onOpenChange,
}: {
  open: boolean;
  login: OnlineLogin | null;
  accountId: string;
  accountName: string;
  defaultRole: OnlineLoginRole;
  people: SeatProfile[];
  /** Change the username, email, and so on. Implies canChangePassword. */
  canEditLogin: boolean;
  canChangePassword: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSaveOnlineLogin();
  const changePassword = useChangeLoginPassword();
  const remove = useDeleteOnlineLogin();
  const [role, setRole] = useState<OnlineLoginRole>(defaultRole);
  const [roleDetail, setRoleDetail] = useState('');
  const [holder, setHolder] = useState(NOBODY);
  const [holderName, setHolderName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState<PasswordMode>('set');
  const [twoFactor, setTwoFactor] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRole(login?.role ?? defaultRole);
    setRoleDetail(login?.role_detail ?? '');
    setHolder(login ? (login.profile_id ?? (login.holder_name ? SOMEONE_ELSE : NOBODY)) : NOBODY);
    setHolderName(login && !login.profile_id ? (login.holder_name ?? '') : '');
    setUsername(login?.username ?? '');
    setEmail(login?.email ?? '');
    setPassword('');
    setPasswordMode(login?.has_password ? 'keep' : 'set');
    setTwoFactor(login?.two_factor ?? '');
    setNotes(login?.notes ?? '');
    setConfirmDelete(false);
  }, [open, login, defaultRole]);

  const passwordOnly = !!login && !canEditLogin;
  const hasIdentity = username.trim().length > 0 || email.trim().length > 0;
  const passwordChange: PasswordChange =
    passwordMode === 'clear'
      ? { kind: 'clear' }
      : passwordMode === 'set' && password.length > 0
        ? { kind: 'set', password }
        : { kind: 'keep' };
  const ready = passwordOnly ? passwordChange.kind !== 'keep' : hasIdentity;
  const pending = save.isPending || changePassword.isPending || remove.isPending;
  const holderOptions =
    login?.profile_id && !people.some((p) => p.id === login.profile_id)
      ? [...people, { id: login.profile_id, full_name: login.holder_name, email: '' }]
      : people;

  const pickHolder = (value: string) => {
    setHolder(value);
    if (!email.trim() && value !== NOBODY && value !== SOMEONE_ELSE) {
      const person = people.find((p) => p.id === value);
      if (person?.email) setEmail(person.email);
    }
  };

  const submit = () => {
    if (passwordOnly && login) {
      void changePassword
        .mutateAsync({ loginId: login.id, change: passwordChange })
        .then(() => {
          toast.success(passwordChange.kind === 'clear' ? 'Password removed.' : 'Password saved.');
          onOpenChange(false);
        })
        .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not save the password.')));
      return;
    }
    void save
      .mutateAsync({
        id: login?.id,
        accountId,
        role,
        roleDetail,
        profileId: holder === NOBODY || holder === SOMEONE_ELSE ? null : holder,
        holderName: holder === SOMEONE_ELSE ? holderName : '',
        username,
        email,
        twoFactor,
        notes,
        password: passwordChange,
      })
      .then(() => {
        toast.success(login ? 'Login updated.' : 'Login added.');
        onOpenChange(false);
      })
      .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not save the login.')));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <form
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) submit();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {passwordOnly && login
                ? `Change password for ${loginLabel(login)}`
                : login
                  ? 'Edit login'
                  : role === 'admin'
                    ? 'Add the admin login'
                    : 'Add a user login'}
            </DialogTitle>
            <DialogDescription>
              {passwordOnly
                ? `${accountName}. Only the password can be changed with your access.`
                : `A sign-in for ${accountName}. Each user on the account gets their own.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {!passwordOnly && (
              <FormSection title="Login">
                <Field label="Type">
                  <Select value={role} onValueChange={(v) => setRole(v as OnlineLoginRole)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{LOGIN_ROLE_LABELS.admin}</SelectItem>
                      <SelectItem value="user">{LOGIN_ROLE_LABELS.user}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Role on the account" htmlFor="login-role-detail" hint="Optional.">
                  <Input
                    id="login-role-detail"
                    value={roleDetail}
                    onChange={(e) => setRoleDetail(e.target.value)}
                    placeholder={role === 'admin' ? 'e.g. Owner' : 'e.g. Billing, Editor'}
                  />
                </Field>
                <Field label="Who uses it" className="sm:col-span-2">
                  <Select value={holder} onValueChange={pickHolder}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NOBODY}>Shared, or nobody in particular</SelectItem>
                      {holderOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.full_name || p.email}
                        </SelectItem>
                      ))}
                      <SelectItem value={SOMEONE_ELSE}>Someone not in Atlas…</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {holder === SOMEONE_ELSE && (
                  <Field label="Their name" htmlFor="login-holder-name" className="sm:col-span-2">
                    <Input
                      id="login-holder-name"
                      value={holderName}
                      onChange={(e) => setHolderName(e.target.value)}
                      placeholder="e.g. Bookkeeper, agency"
                    />
                  </Field>
                )}
                <Field label="Username" htmlFor="login-username">
                  <Input
                    id="login-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    data-1p-ignore
                    data-lpignore="true"
                  />
                </Field>
                <Field label="Email" htmlFor="login-email">
                  <Input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    data-1p-ignore
                    data-lpignore="true"
                  />
                </Field>
                {!hasIdentity && (
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    Enter a username, an email, or both.
                  </p>
                )}
              </FormSection>
            )}

            <FormSection
              title="Password"
              description="Encrypted when saved. Viewing or copying it is recorded in the audit log."
            >
              {login?.has_password && (
                <div className="flex flex-wrap gap-2 sm:col-span-2">
                  {(['keep', 'set', 'clear'] as const).map((mode) => (
                    <Button
                      key={mode}
                      type="button"
                      size="sm"
                      variant={passwordMode === mode ? 'default' : 'outline'}
                      onClick={() => setPasswordMode(mode)}
                    >
                      {mode === 'keep' ? 'Keep current' : mode === 'set' ? 'Change' : 'Remove'}
                    </Button>
                  ))}
                </div>
              )}
              {passwordMode === 'keep' && login?.password_updated_at && (
                <p className="text-sm text-muted-foreground sm:col-span-2">
                  Last changed {formatDate(login.password_updated_at)}.
                </p>
              )}
              {passwordMode === 'set' && (
                <Field
                  label={login?.has_password ? 'New password' : 'Password'}
                  htmlFor="login-password"
                  className="sm:col-span-2"
                  hint={login || passwordOnly ? undefined : 'Optional. You can add it later.'}
                >
                  <PasswordInput
                    id="login-password"
                    value={password}
                    onChange={setPassword}
                    autoFocus={passwordOnly}
                  />
                </Field>
              )}
              {passwordMode === 'clear' && (
                <p className="text-sm text-muted-foreground sm:col-span-2">
                  The saved password will be deleted. The account itself is not changed.
                </p>
              )}
            </FormSection>

            {!passwordOnly && (
              <FormSection title="More">
                <Field
                  label="Two-step verification"
                  htmlFor="login-2fa"
                  className="sm:col-span-2"
                  hint="Where the codes go, so the next person can get in."
                >
                  <Input
                    id="login-2fa"
                    value={twoFactor}
                    onChange={(e) => setTwoFactor(e.target.value)}
                    placeholder="e.g. Texts to the office phone, Google Authenticator on Sam’s phone"
                  />
                </Field>
                <Field label="Notes" htmlFor="login-notes" className="sm:col-span-2">
                  <Textarea
                    id="login-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                </Field>
              </FormSection>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {canEditLogin && login ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync(login.id)
                      .then(() => {
                        toast.success('Login removed.');
                        onOpenChange(false);
                      })
                      .catch((err) =>
                        toast.error(friendlyOnlineAccountError(err, 'Could not remove the login.')),
                      )
                  }
                >
                  Remove login and password
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Remove login
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {(canEditLogin || canChangePassword) && (
                <Button type="submit" disabled={pending || !ready}>
                  {passwordOnly ? 'Save password' : login ? 'Save' : 'Add login'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
